<app-header />

<div class="min-h-screen bg-gray-50 flex items-start justify-center pt-8 pb-12 px-4 sm:px-6 lg:px-8">
  <div class="max-w-md w-full space-y-8">
    
    <!-- Success Message: Registration Complete -->
    @if (registrationSuccess) {
      <div class="bg-white rounded-lg shadow-lg p-8">
        <!-- Success Icon -->
        <div class="flex justify-center mb-6">
          <div class="rounded-full bg-green-100 p-3">
            <svg class="w-16 h-16 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 19v-8.93a2 2 0 01.89-1.664l7-4.666a2 2 0 012.22 0l7 4.666A2 2 0 0121 10.07V19M3 19a2 2 0 002 2h14a2 2 0 002-2M3 19l6.75-4.5M21 19l-6.75-4.5M3 10l6.75 4.5M21 10l-6.75 4.5m0 0l-1.14.76a2 2 0 01-2.22 0l-1.14-.76" />
            </svg>
          </div>
        </div>

        <!-- Title -->
        <h2 class="text-center text-2xl font-bold text-gray-900 mb-4">
          ¡Registro Exitoso!
        </h2>

        <!-- Message -->
        <div class="text-center space-y-4 mb-6">
          <p class="text-gray-700">
            Hemos enviado un correo de activación a:
          </p>
          <p class="text-lg font-semibold text-[#e8343f]">
            {{ registeredEmail }}
          </p>
          <p class="text-gray-600">
            {{ registrationMessage || 'Por favor, revisa tu bandeja de entrada y haz clic en el enlace de activación para completar tu registro.' }}
          </p>
        </div>

        <!-- Instructions -->
        <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div class="flex">
            <svg class="h-5 w-5 text-blue-600 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div class="ml-3 flex-1">
              <h3 class="text-sm font-medium text-blue-900 mb-2">
                Instrucciones importantes:
              </h3>
              <ul class="text-sm text-blue-800 space-y-1 list-disc list-inside">
                <li>El enlace de activación es válido por 24 horas</li>
                <li>Revisa tu carpeta de spam si no lo recibes en 5 minutos</li>
                <li>Una vez activada tu cuenta, podrás iniciar sesión</li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="space-y-3">
          <button
            (click)="goToLogin()"
            class="w-full py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-[#e8343f] hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#e8343f] transition-colors"
          >
            Ir a Iniciar Sesión
          </button>
          
          <button
            type="button"
            (click)="registrationSuccess = false; currentStep = 1; emailForm.reset(); registerForm.reset();"
            class="w-full py-3 px-4 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#e8343f] transition-colors"
          >
            Registrar Otra Cuenta
          </button>
        </div>

        <!-- Help Text -->
        <div class="mt-6 text-center">
          <p class="text-xs text-gray-500">
            ¿No recibiste el correo? Espera unos minutos y revisa tu carpeta de spam.
          </p>
        </div>
      </div>
    } @else {

    <!-- Logo/Header -->
    <div>
      <h2 class="mt-6 text-center text-3xl font-extrabold text-gray-900">
        Crear Cuenta
      </h2>
      <p class="mt-2 text-center text-sm text-gray-600">
        @if (currentStep === 1) {
          Ingresa tu email para comenzar
        } @else {
          Completa tu información personal
        }
      </p>
    </div>

    <!-- Progress Indicator -->
    <div class="flex items-center justify-center space-x-4">
      <div class="flex items-center">
        <div [class]="currentStep >= 1 ? 'w-8 h-8 bg-[#e8343f] text-white rounded-full flex items-center justify-center font-semibold' : 'w-8 h-8 bg-gray-300 text-gray-600 rounded-full flex items-center justify-center font-semibold'">
          1
        </div>
        <span class="ml-2 text-sm font-medium" [class.text-gray-900]="currentStep >= 1" [class.text-gray-500]="currentStep < 1">Email</span>
      </div>
      <div class="w-12 h-1" [class.bg-[#e8343f]]="currentStep >= 2" [class.bg-gray-300]="currentStep < 2"></div>
      <div class="flex items-center">
        <div [class]="currentStep >= 2 ? 'w-8 h-8 bg-[#e8343f] text-white rounded-full flex items-center justify-center font-semibold' : 'w-8 h-8 bg-gray-300 text-gray-600 rounded-full flex items-center justify-center font-semibold'">
          2
        </div>
        <span class="ml-2 text-sm font-medium" [class.text-gray-900]="currentStep >= 2" [class.text-gray-500]="currentStep < 2">Datos</span>
      </div>
    </div>

    <!-- Step 1: Email Verification -->
    @if (currentStep === 1) {
      <form class="mt-8 space-y-6" [formGroup]="emailForm" (ngSubmit)="onCheckEmail()">
        <div class="rounded-md shadow-sm space-y-4">
          <!-- Email -->
          <div>
            <label for="email" class="block text-sm font-medium text-gray-700 mb-1">
              Email <span class="text-red-500">*</span>
            </label>
            <input
              id="email"
              name="email"
              type="email"
              formControlName="email"
              class="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-[#e8343f] focus:border-[#e8343f] focus:z-10 sm:text-sm"
              [class.border-red-500]="isFieldInvalid(emailForm, 'email')"
              placeholder="ejemplo@correo.com"
            />
            @if (isFieldInvalid(emailForm, 'email')) {
              <p class="mt-1 text-sm text-red-600">{{ getFieldError(emailForm, 'email') }}</p>
            }
          </div>
        </div>

        <!-- Error Message -->
        @if (errorMessage) {
          <div class="rounded-md bg-red-50 p-4">
            <div class="flex">
              <div class="flex-shrink-0">
                <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                </svg>
              </div>
              <div class="ml-3">
                <p class="text-sm font-medium text-red-800">{{ errorMessage }}</p>
              </div>
            </div>
          </div>
        }

        <!-- Cloudflare Turnstile -->
        <div class="flex justify-center">
          <div id="turnstile-container-step1"></div>
        </div>

        <!-- Submit Button -->
        <div>
          <button
            type="submit"
            [disabled]="emailChecking || !turnstileToken"
            class="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-[#e8343f] hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#e8343f] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            @if (emailChecking) {
              <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Verificando...
            } @else {
              Continuar
            }
          </button>
        </div>

        <!-- Link to Login -->
        <div class="text-center text-sm">
          <span class="text-gray-600">¿Ya tienes cuenta? </span>
          <a routerLink="/login" class="font-medium text-[#e8343f] hover:text-red-700">
            Inicia sesión
          </a>
        </div>
      </form>
    }

    <!-- Step 2: Complete Registration -->
    @if (currentStep === 2) {
      <form class="mt-8 space-y-6" [formGroup]="registerForm" (ngSubmit)="onSubmitRegister()">
        <div class="rounded-md shadow-sm space-y-4">
          <!-- Firstname -->
          <div>
            <label for="firstname" class="block text-sm font-medium text-gray-700 mb-1">
              Nombre <span class="text-red-500">*</span>
            </label>
            <input
              id="firstname"
              name="firstname"
              type="text"
              formControlName="firstname"
              class="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-[#e8343f] focus:border-[#e8343f] focus:z-10 sm:text-sm uppercase"
              [class.border-red-500]="isFieldInvalid(registerForm, 'firstname')"
              placeholder="JUAN"
            />
            @if (isFieldInvalid(registerForm, 'firstname')) {
              <p class="mt-1 text-sm text-red-600">{{ getFieldError(registerForm, 'firstname') }}</p>
            }
          </div>

          <!-- Lastname -->
          <div>
            <label for="lastname" class="block text-sm font-medium text-gray-700 mb-1">
              Apellido <span class="text-red-500">*</span>
            </label>
            <input
              id="lastname"
              name="lastname"
              type="text"
              formControlName="lastname"
              class="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-[#e8343f] focus:border-[#e8343f] focus:z-10 sm:text-sm uppercase"
              [class.border-red-500]="isFieldInvalid(registerForm, 'lastname')"
              placeholder="PÉREZ"
            />
            @if (isFieldInvalid(registerForm, 'lastname')) {
              <p class="mt-1 text-sm text-red-600">{{ getFieldError(registerForm, 'lastname') }}</p>
            }
          </div>

          <!-- Phone -->
          <div>
            <label for="phone" class="block text-sm font-medium text-gray-700 mb-1">
              Teléfono <span class="text-red-500">*</span>
            </label>
            <div class="phone-input-container">
              <div class="country-selector">
                <button type="button" class="country-selector-button"
                  (click)="toggleCountryDropdown($event)">
                  <span class="fi fi-{{ getSelectedCountry()?.code?.toLowerCase() }}"></span>
                  <span class="dial-code">{{ getSelectedCountry()?.dialCode }}</span>
                  <svg xmlns="http://www.w3.org/2000/svg" class="chevron-icon" fill="none"
                    viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                <div class="country-dropdown" *ngIf="showCountryDropdown" (click)="$event.stopPropagation()">
                  <div class="country-list">
                    <button type="button" *ngFor="let country of countries"
                      class="country-option" (click)="selectCountry(country); $event.stopPropagation()">
                      <span class="fi fi-{{ country.code.toLowerCase() }}"></span>
                      <span class="country-name">{{ country.name }}</span>
                      <span class="dial-code">{{ country.dialCode }}</span>
                    </button>
                  </div>
                </div>
              </div>
              <input
                id="phone"
                name="phone"
                type="tel"
                formControlName="phone"
                (input)="onPhoneInput()"
                class="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-r-lg focus:outline-none focus:ring-[#e8343f] focus:border-[#e8343f] focus:z-10 sm:text-sm phone-number-input"
                [class.border-red-500]="isFieldInvalid(registerForm, 'phone') || validatePhoneField()"
                [placeholder]="getPhonePlaceholder()"
                [maxlength]="getPhoneMaxLength()"
              />
            </div>
            @if (isFieldInvalid(registerForm, 'phone')) {
              <p class="mt-1 text-sm text-red-600">{{ getFieldError(registerForm, 'phone') }}</p>
            }
            @if (validatePhoneField() && !isFieldInvalid(registerForm, 'phone')) {
              <p class="mt-1 text-sm text-red-600">{{ validatePhoneField() }}</p>
            }
          </div>

          <!-- Password -->
          <div>
            <label for="password" class="block text-sm font-medium text-gray-700 mb-1">
              Contraseña <span class="text-red-500">*</span>
            </label>
            <div class="relative">
              <input
                id="password"
                name="password"
                [type]="showPassword ? 'text' : 'password'"
                formControlName="password"
                class="appearance-none relative block w-full px-3 py-2 pr-10 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-[#e8343f] focus:border-[#e8343f] focus:z-10 sm:text-sm"
                [class.border-red-500]="isFieldInvalid(registerForm, 'password')"
                placeholder="Mínimo 8 caracteres"
              />
              <button
                type="button"
                (click)="togglePasswordVisibility()"
                class="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                <svg *ngIf="!showPassword" class="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                <svg *ngIf="showPassword" class="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                </svg>
              </button>
            </div>
            @if (isFieldInvalid(registerForm, 'password')) {
              <p class="mt-1 text-sm text-red-600">{{ getFieldError(registerForm, 'password') }}</p>
            }
            
            <!-- Password Strength Indicator -->
            @if (registerForm.get('password')?.value) {
              <div class="mt-2">
                <div class="flex items-center space-x-2">
                  <div class="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      class="h-full transition-all duration-300"
                      [class.w-1/3]="passwordStrength === 'weak'"
                      [class.w-2/3]="passwordStrength === 'medium'"
                      [class.w-full]="passwordStrength === 'strong'"
                      [class.bg-red-500]="passwordStrength === 'weak'"
                      [class.bg-yellow-500]="passwordStrength === 'medium'"
                      [class.bg-green-500]="passwordStrength === 'strong'"
                    ></div>
                  </div>
                  <span class="text-xs font-medium"
                    [class.text-red-500]="passwordStrength === 'weak'"
                    [class.text-yellow-500]="passwordStrength === 'medium'"
                    [class.text-green-500]="passwordStrength === 'strong'"
                  >
                    @if (passwordStrength === 'weak') { Débil }
                    @if (passwordStrength === 'medium') { Media }
                    @if (passwordStrength === 'strong') { Fuerte }
                  </span>
                </div>
              </div>
            }
          </div>

          <!-- Confirm Password -->
          <div>
            <label for="confirmPassword" class="block text-sm font-medium text-gray-700 mb-1">
              Confirmar Contraseña <span class="text-red-500">*</span>
            </label>
            <div class="relative">
              <input
                id="confirmPassword"
                name="confirmPassword"
                [type]="showConfirmPassword ? 'text' : 'password'"
                formControlName="confirmPassword"
                class="appearance-none relative block w-full px-3 py-2 pr-10 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-[#e8343f] focus:border-[#e8343f] focus:z-10 sm:text-sm"
                [class.border-red-500]="isFieldInvalid(registerForm, 'confirmPassword') || getPasswordMatchError()"
                placeholder="Repite tu contraseña"
              />
              <button
                type="button"
                (click)="toggleConfirmPasswordVisibility()"
                class="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                <svg *ngIf="!showConfirmPassword" class="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                <svg *ngIf="showConfirmPassword" class="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                </svg>
              </button>
            </div>
            @if (getPasswordMatchError()) {
              <p class="mt-1 text-sm text-red-600">{{ getPasswordMatchError() }}</p>
            }
          </div>
        </div>

        <!-- Error Message -->
        @if (errorMessage) {
          <div class="rounded-md bg-red-50 p-4">
            <div class="flex">
              <div class="flex-shrink-0">
                <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                </svg>
              </div>
              <div class="ml-3">
                <p class="text-sm font-medium text-red-800">{{ errorMessage }}</p>
              </div>
            </div>
          </div>
        }

        <!-- Cloudflare Turnstile -->
        <div class="flex justify-center">
          <div id="turnstile-container-step2"></div>
        </div>

        <!-- Buttons -->
        <div class="flex space-x-4">
          <button
            type="button"
            (click)="goBackToEmailStep()"
            class="flex-1 py-2 px-4 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#e8343f] transition-colors"
          >
            Atrás
          </button>
          <button
            type="submit"
            [disabled]="isLoading || !turnstileToken"
            class="flex-1 py-2 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-[#e8343f] hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#e8343f] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            @if (isLoading) {
              <span class="flex items-center justify-center">
                <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Registrando...
              </span>
            } @else {
              Crear Cuenta
            }
          </button>
        </div>
      </form>
    }
    } <!-- End of @else -->
  </div>
</div>

<app-footer />
